<script setup lang="ts">
import type { Member } from '@ilteducation/content-collections-types';
import type { User, Group } from '@ilteducation/customer-mgmt';
import { useDisplay } from 'vuetify';
import MembersControl from './MembersControl.vue';
import SharingOptions from './SharingOptions.vue';
import type { SharingOptionItems } from '../types/share';
import useLocale from '../helpers/use-locale';

type Emits = {
  (e: 'addMembers'): void;
  (e: 'addOwners'): void;
  (e: 'removeMember', id: string): void;
  (e: 'removeAllMembers'): void;
  (e: 'removeOwner', id: string): void;
  (e: 'openQrCode'): void;
  (e: 'shareByEmail'): void;
  (e: 'copyUrl'): void;
};

const emit = defineEmits<Emits>();

type Props = {
  entityTitle?: string;
  members: Member[];
  owners: Member[];
  entityUrl?: string;
  entityShareUrl?: string;
  fetchUsers: (ids: string[]) => Promise<User[]>;
  fetchGroups: (ids: string[]) => Promise<Group[]>;
  hideSharingOptions?: boolean;
  showOwners?: boolean;
  canAddMembers?: boolean;
  canRemoveOwners?: boolean;
  canRemoveMembers?: boolean;
  isAvailableToShare?: boolean;
  sharingOptions?: SharingOptionItems[];
  withDefaultSharingOptions: boolean;
  rounded?: 'sm' | 'default' | 'lg';
  density?: 'comfortable' | 'compact' | 'default';
  isLoading?: boolean;
};

withDefaults(defineProps<Props>(), {
  sharingOptions: () => [],
  members: () => [],
  owners: () => [],
  showOwners: true,
  canAddMembers: true,
  canRemoveOwners: true,
  canRemoveMembers: true,
  isAvailableToShare: true,
  withDefaultSharingOptions: true,
  isLoading: false,
  rounded: 'default',
  density: 'default',
});

const { t } = useLocale();
const { smAndDown } = useDisplay();
</script>

<template>
  <div>
    <p class="font-weight-bold text-h6" :class="smAndDown ? 'mb-0' : 'mb-5'">
      <slot name="top"></slot>
    </p>
    <MembersControl
      v-if="showOwners"
      class="mb-6"
      :title="t('components.owners')"
      :participators="owners"
      :can-add="canAddMembers"
      :can-remove="canRemoveOwners"
      :fetch-groups="fetchGroups"
      :fetch-users="fetchUsers"
      :rounded="rounded"
      :density="density"
      @onClickAdd="() => emit('addOwners')"
      @onClickRemove="(id) => emit('removeOwner', id)"
    />
    <MembersControl
      :class="smAndDown ? 'mb-0' : 'mb-6'"
      :title="t('components.members')"
      :participators="members"
      :can-add="canAddMembers"
      :can-remove="canRemoveMembers"
      :can-remove-all="canAddMembers"
      :fetch-groups="fetchGroups"
      :fetch-users="fetchUsers"
      :is-loading="isLoading"
      :rounded="rounded"
      :density="density"
      @onClickAdd="() => emit('addMembers')"
      @onClickRemove="(id: string) => emit('removeMember', id)"
      @onClickRemoveAll="emit('removeAllMembers')"
    />
    <SharingOptions
      v-if="!hideSharingOptions"
      :entity-title="entityTitle"
      :entity-url="entityUrl"
      :share-entity-url="entityShareUrl"
      :with-default-options="withDefaultSharingOptions"
      :sharing-items="sharingOptions ?? []"
      :is-available-to-share="isAvailableToShare"
      :rounded="rounded"
      content="book"
      @open-qrcode="emit('openQrCode')"
      @share-by-email="emit('shareByEmail')"
      @copy-url="emit('copyUrl')"
    >
      <template #title-right>
        <slot name="title-right"></slot>
      </template>
      <template #subtitle>
        <slot name="subtitle"></slot>
      </template>
    </SharingOptions>
  </div>
</template>

<style scoped lang="scss"></style>
